import { Component, ElementRef, HostBinding, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';

export interface EnfafSelectOption {
  value: string | number | null;
  viewValue: string;
}

@Component({
  selector: 'enfaf-select',
  imports: [
    CommonModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule
  ],
  templateUrl: './enfaf-select.component.html',
  styleUrls: ['./enfaf-select.component.scss']
})
export class EnfafSelectComponent implements OnInit {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = 'Selecciona uno';
  @Input() errorLabel: string = '';
  @Input() control: any;
  @Input() options: EnfafSelectOption[] = [];
  @Input() searchable: boolean = false; // Enable/disable search
  @Input() color: 'outline' | 'primary' = 'outline';
  @Input() withoutHint: boolean = false;
  /** Extra CSS class(es) for the dropdown panel (optional). */
  @Input() panelClass: string | string[] = '';

  private _disabled: boolean = false;
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.renderer.addClass(this.elementRef.nativeElement, 'enfaf-select--disabled');
    } else {
      this.renderer.removeClass(this.elementRef.nativeElement, 'enfaf-select--disabled');
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  @HostBinding('attr.inert')
  get inertAttr(): string | null {
    return this._disabled ? '' : null;
  }

  /**
   * Panel classes. Toolbar-style selects (`color="primary"`) with 6-10 options get a taller panel so the
   * last option is not cut off by Material's 275px default (6 x 48px = 288px).
   */
  get panelClasses(): string[] {
    const extra = Array.isArray(this.panelClass) ? this.panelClass : this.panelClass ? [this.panelClass] : [];
    const fit = this.color === 'primary' && this.options.length > 5 && this.options.length <= 10;
    return fit ? [...extra, 'enfaf-select-panel--fit'] : extra;
  }

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  filteredOptions$: Observable<EnfafSelectOption[]> = of([]);
  searchControl = new FormControl('');

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    if (this.searchable) {
      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm: string | null) => {
          if (!searchTerm) {
            return of(this.options);
          }

          const filtered = this.options.filter((option) => option.viewValue.toLowerCase().includes((searchTerm || '').toLowerCase()));
          return of(filtered);
        })
      );
    } else {
      this.filteredOptions$ = of(this.options);
    }
  }

  onOptionSelected(option: EnfafSelectOption): void {
    this.control.setValue(option.value);
    this.searchControl.setValue('');
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.closePanel();
    }
  }

  getSelectedValueText(): string {
    if (!this.control?.value) return '';
    const option = this.options.find((opt) => opt.value === this.control.value);
    return option ? option.viewValue : '';
  }

  displayFn(value: string | number | null): string {
    if (!value) return '';
    const option = this.options.find((opt) => opt.value === value);
    return option ? option.viewValue : '';
  }
}

/**
 * Usage examples:
 *
 * // Without search (original behavior)
 * <enfaf-select
 *   [label]="'Category'"
 *   [control]="categoryControl"
 *   [options]="categoryOptions"
 * ></enfaf-select>
 *
 * // With search enabled
 * <enfaf-select
 *   [label]="'Select Category'"
 *   [control]="categoryControl"
 *   [options]="categoryOptions"
 *   [searchable]="true"
 * ></enfaf-select>
 *
 * // With error handling
 * <enfaf-select
 *   [label]="'Category'"
 *   [control]="categoryControl"
 *   [options]="categoryOptions"
 *   [searchable]="true"
 *   [errorLabel]="'Category is required'"
 * ></enfaf-select>
 */
